import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, RotateCcw, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function DisqualifiedScreen({ reason, onRestart, snapshot }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [joined, setJoined] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/waitlist", {
        name: name.trim() || undefined,
        email: email.trim(),
        note: reason,
        monthly_revenue: snapshot?.monthly_revenue ?? undefined,
        credit_score: snapshot?.credit_score ?? undefined,
      });
      setJoined(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="py-6"
      data-testid="disqualified-screen"
    >
      <div className="w-12 h-12 rounded-sm bg-rose-500/10 border border-rose-500/30 flex items-center justify-center mb-6">
        <ShieldAlert className="w-6 h-6 text-rose-400" />
      </div>
      <h3 className="font-display text-2xl font-medium tracking-tight text-zinc-100 mb-3">
        We cannot move forward at this time.
      </h3>
      <p className="text-sm leading-relaxed text-zinc-400 max-w-lg" data-testid="disqualified-message">
        Based on our strict lender underwriting guidelines, we cannot move forward with your
        application at this time.
      </p>
      <p className="mt-4 text-xs leading-relaxed text-zinc-500 border-l-2 border-amber-500/40 pl-3 max-w-lg">
        Guidelines shift and files strengthen. Leave your details and a broker will reach out the
        moment your profile clears — typically within a few quarters.
      </p>

      {joined ? (
        <div className="mt-8 flex items-start gap-3 text-emerald-400" data-testid="waitlist-joined">
          <CheckCircle2 className="w-5 h-5 shrink-0" />
          <div>
            <p className="text-sm">You're on the list. Your roadmap is on its way to your inbox.</p>
            <p className="text-xs text-zinc-500 mt-1">
              We'll check back in at 30 and 90 days with a one-click re-check link.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={join} className="mt-8 space-y-3 max-w-md" data-testid="waitlist-form">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name (optional)"
            data-testid="waitlist-name-input"
            className="w-full bg-[#07090D] border border-white/15 rounded-sm px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 transition-colors"
          />
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@business.com"
            data-testid="waitlist-email-input"
            className="w-full bg-[#07090D] border border-white/15 rounded-sm px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-amber-500/60 transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="waitlist-submit-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-[#07090D] font-semibold text-sm px-6 py-3.5 rounded-sm transition-all duration-300 active:scale-[0.98]"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Join the priority waitlist
          </button>
        </form>
      )}

      <button
        onClick={onRestart}
        data-testid="disqualified-restart-button"
        className="mt-8 inline-flex items-center gap-2 text-xs font-mono-d tracking-[0.15em] uppercase text-zinc-500 hover:text-amber-400 transition-colors duration-300"
      >
        <RotateCcw className="w-3.5 h-3.5" /> Start over
      </button>
    </motion.div>
  );
}
